import { useRecoilValue } from 'recoil';
import { useTranslation } from '../../../contexts/TranslationContext';
import { DisclaimerText } from '../MHM/MHMTypography';
import { DeviceInfoSection } from './DeviceInfoSection';
import { selectedDeviceStateAtom } from '../../../models/atoms/device_atoms';
import {
  get_signal_strength_value,
  unix_timestamp_to_date_time,
} from '../../../utils/helper_functions';

export function DeviceTransmissions() {
  const { t, language, temperature } = useTranslation();
  const selectedDevice = useRecoilValue(selectedDeviceStateAtom);

  if (!selectedDevice) {
    return null;
  }

  const lastTramissionTime = unix_timestamp_to_date_time(
    selectedDevice.last_transmission_time,
    true,
    true,
    false,
    language,
  );

  // Use count fields if available, fallback to array length for backwards compatibility
  const allMissedCount = selectedDevice.all_missed_transmissions_count;
  const sessionMissedCount = selectedDevice.session_missed_transmissions_count;

  const totalTransmissionsCount =
    allMissedCount + selectedDevice.total_messages_received;

  const missedTransmissionsPercentage =
    totalTransmissionsCount === 0
      ? 0
      : Math.round((allMissedCount / totalTransmissionsCount) * 100);

  const successfulTransmissionsPercentage =
    totalTransmissionsCount === 0
      ? 100
      : Math.round(
          (selectedDevice.total_messages_received / totalTransmissionsCount) *
            100,
        );

  const nextTransmissionTime = unix_timestamp_to_date_time(
    selectedDevice.expected_next_transmission,
    true,
    true,
    false,
    language,
  );

  const installationSignalStrength = get_signal_strength_value(
    selectedDevice.install_signal_strength,
    selectedDevice.install_signal_check_time,
  );

  const transmissionSignalStrength = get_signal_strength_value(
    selectedDevice.signal_strength,
    selectedDevice.signal_check_time,
  );

  return (
    <DeviceInfoSection>
      <DeviceInfoSection.Heading>
        {t('Transmissions')}
      </DeviceInfoSection.Heading>

      <DisclaimerText>
        {t(
          'Missed transmissions and next transmission are calculated based on defined normal operation settings.',
        )}
      </DisclaimerText>

      <DeviceInfoSection.TextEntry
        label={t('Sim ID: ')}
        text={selectedDevice.sim_id.split(',')[0].replace(/["',]/g, '')}
      />
      <DeviceInfoSection.TextEntry
        label={t('Modem Mode:')}
        text={selectedDevice.modem_mode}
      />
      <DeviceInfoSection.TextEntry
        label={t('Antenna:')}
        text={t(selectedDevice.antenna_type)}
      />
      <DeviceInfoSection.TextEntry
        label={t('Last Transmission:')}
        text={lastTramissionTime}
      />
      <DeviceInfoSection.TextEntry
        label={t('Next Transmission:')}
        text={nextTransmissionTime}
      />
      <DeviceInfoSection.TextEntry
        label={t('Total Transmissions:')}
        text={totalTransmissionsCount}
      />
      <DeviceInfoSection.TextEntry
        label={t('Transmissions Successful:')}
        text={`${selectedDevice.total_messages_received} (${successfulTransmissionsPercentage}%)`}
      />
      <DeviceInfoSection.TextEntry
        label={t('Transmissions Missed (current session):')}
        text={sessionMissedCount}
      />
      <DeviceInfoSection.TextEntry
        label={t('Transmissions Missed (lifetime):')}
        text={`${allMissedCount} (${missedTransmissionsPercentage}%)`}
      />
      <DeviceInfoSection.TextEntry
        label={t('Installation Signal Strength: ')}
        text={`${installationSignalStrength[1]}% (${t(installationSignalStrength[0])})`}
        textProps={{
          color: installationSignalStrength[2],
          medium: true,
        }}
      />
      <DeviceInfoSection.TextEntry
        label={t('Last Transmission Signal Strength: ')}
        text={`${transmissionSignalStrength[1]}% (${t(transmissionSignalStrength[0])})`}
        textProps={{
          color: transmissionSignalStrength[2],
          medium: true,
        }}
      />
      <DeviceInfoSection.TextEntry
        label={t('Last Transmission Temperature: ')}
        text={
          selectedDevice.temperature || selectedDevice.temperature === 0
            ? temperature.show(selectedDevice.temperature)
            : t('Not available')
        }
        textProps={{
          medium: true,
        }}
      />
    </DeviceInfoSection>
  );
}
